/**
 * Canonical data for the nine grahas. One source of truth used by both the
 * home-page navagraha carousel and the per-graha detail pages.
 *
 * Content voice follows the founder's rule: attributions are stated AS
 * attributions ("classical sources associate…"), never as effects. No health,
 * financial or outcome claims anywhere.
 *
 * The `stones` / `rudraksha_mukhi` fields are shopping-hints — the actual
 * shop grid on the detail page is populated live from /api/products?graha=X.
 */

export const GRAHA_DETAILS = [
  {
    slug: "surya",
    graha: "Surya",
    planet: "Sun",
    deva: "सूर्य",
    vahana: "A chariot drawn by seven white horses",
    presiding_form: "Surya Narayana",
    day: "Sunday",
    direction: "East",
    rashis: ["simha"],
    principal_stone: "ruby",
    upratna: ["red-garnet", "sunstone"],
    catalog_slug: "sun",
    stone_slug: "ruby",
    stone_name: "Ruby",
    stone_hindi: "Manik",
    accent: "#8a2f22",
    pitch:
      "Ruby is the stone the classical scheme reads first under Surya. Every ruby we hold is a one-of-one piece — origin stated, treatment stated, priced per carat in the open — and the report number checks out on the laboratory's own website before you pay a rupee.",
    rudraksha_mukhi: [1, 12],
    mantra: "ॐ घृणि सूर्याय नमः",
    mantra_roman: "Oṁ Ghṛṇi Sūryāya Namaḥ",
    lede:
      "Surya is the graha of vitality, self-command and leadership in the classical scheme. In a birth chart Surya carries the sense of the self before the world — where a person's presence is felt.",
    knowledge: [
      "Classical sources place Surya at the centre of the graha scheme. The Sanskrit for planet — graha — is not the same word as the Latin planet; a graha is a 'seizer', something that holds attention. Surya is the first, and every other graha is measured against him.",
      "Where Surya is well-placed in a chart, the classical texts describe steadiness of purpose and clarity in leadership. Where Surya is afflicted, the same texts describe the opposite. Neither statement is an outcome claim — they are what the classical sources say, and we quote them here as attributions rather than promises.",
      "Surya's day is Sunday, his direction east, and his presiding form Surya Narayana. The seven horses of his chariot represent the seven meters of Vedic recitation, or the seven days of the week — the sources give both readings and we do not pick between them.",
    ],
    why_it_is_worn:
      "Classical sources associate ruby with Surya. It is the stone traditionally given at the start of leadership responsibilities and to one whose chart shows a strong but underexpressed Sun. The wording is deliberate: classical sources associate — not this stone brings success.",
  },
  {
    slug: "chandra",
    graha: "Chandra",
    planet: "Moon",
    deva: "चन्द्र",
    vahana: "A chariot drawn by a pair of white antelopes",
    presiding_form: "Parvati",
    day: "Monday",
    direction: "North-west",
    rashis: ["karka"],
    principal_stone: "pearl",
    upratna: ["moonstone", "white-sapphire"],
    catalog_slug: "moon",
    stone_slug: "pearl",
    stone_name: "Pearl",
    stone_hindi: "Moti",
    accent: "#6f7686",
    pitch:
      "Pearl is the stone the tradition places under Chandra — the first stone most families ever buy. Ours are certified, priced in the open, and each listing states exactly what it is: origin, surface, lustre, and a report you can verify without going through us.",
    rudraksha_mukhi: [2],
    mantra: "ॐ सोम सोमाय नमः",
    mantra_roman: "Oṁ Soma Somāya Namaḥ",
    lede:
      "Chandra is the graha of mind, memory and the tides of mood — the fastest-moving graha in the classical scheme, and the one most read for the mental state of the wearer.",
    knowledge: [
      "Chandra is second only to Surya in the classical hierarchy. Where the Sun is the self as it is seen, the Moon is the mind as it experiences itself. Vedic astrology reads Chandra as intensely as it reads the Sun, and often more.",
      "Because Chandra moves quickly through the zodiac — about one rashi every two-and-a-half days — his placement colours short-term experience more than any other graha. The classical texts caution that a strong Chandra makes a difficult mind bearable and a difficult mind unbearable in equal measure.",
      "Chandra's day is Monday, his direction north-west, and his presiding form Parvati (some sources say Uma; the disagreement is old and we say so).",
    ],
    why_it_is_worn:
      "Classical sources associate pearl with Chandra. It is the stone traditionally worn during unsettled periods of mind, and by those whose chart shows a strong lunar placement. We do not say pearl calms the mind — the tradition places it under Chandra.",
  },
  {
    slug: "mangal",
    graha: "Mangal",
    planet: "Mars",
    deva: "मंगल",
    vahana: "A red ram",
    presiding_form: "Kartikeya (Muruga)",
    day: "Tuesday",
    direction: "South",
    rashis: ["mesha", "vrishchika"],
    principal_stone: "red-coral",
    upratna: ["carnelian"],
    catalog_slug: "mangal",
    stone_slug: "red-coral",
    stone_name: "Red Coral",
    stone_hindi: "Moonga",
    accent: "#a03325",
    pitch:
      "Red coral is the Mangal stone in every classical source — the stone given at the start of an undertaking. Each piece we list carries its origin, its treatment and a laboratory report that verifies on the lab's own website, not on ours.",
    rudraksha_mukhi: [3],
    mantra: "ॐ अं अंगारकाय नमः",
    mantra_roman: "Oṁ Aṁ Aṅgārakāya Namaḥ",
    lede:
      "Mangal — Mars — is the graha of action, initiative and courage. He is the one who begins undertakings, and in the classical scheme he is read whenever a chart requires assertion rather than settling.",
    knowledge: [
      "Classical sources place Mangal at the head of the karma-graha triad — the three grahas whose activity shows up in visible action. He is read whenever a chart shows a phase of beginnings, whether that is a career move, a new practice or a departure.",
      "Mangal's day is Tuesday, his direction south, and his presiding form Kartikeya (Muruga in the Tamil tradition — the same deity, the same graha, different regional naming).",
      "A strong Mangal in the classical sources means initiative and unhesitating action. A weak Mangal means hesitation. Neither is a value judgment; both are what the texts say.",
    ],
    why_it_is_worn:
      "Classical sources associate red coral with Mangal. It is the stone traditionally worn at the start of an undertaking that requires assertion. We do not say coral makes the wearer courageous — the tradition places it under Mangal.",
  },
  {
    slug: "budh",
    graha: "Budh",
    planet: "Mercury",
    deva: "बुध",
    vahana: "A golden lion",
    presiding_form: "Vishnu (through Budh as a form)",
    day: "Wednesday",
    direction: "North",
    rashis: ["mithuna", "kanya"],
    principal_stone: "emerald",
    upratna: ["peridot", "green-tourmaline"],
    catalog_slug: "budh",
    stone_slug: "emerald",
    stone_name: "Emerald",
    stone_hindi: "Panna",
    accent: "#1f7a45",
    pitch:
      "Emerald is the Budh stone in the classical scheme — traditionally taken up at the start of a commercial or scholarly undertaking. Every emerald we hold is certified, one-of-one, and listed with the exact per-carat price so you can compare us against anyone.",
    rudraksha_mukhi: [4],
    mantra: "ॐ बुं बुधाय नमः",
    mantra_roman: "Oṁ Buṁ Budhāya Namaḥ",
    lede:
      "Budh is the graha of speech, commerce and clear thought. He is the youngest of the classical grahas in appearance, and the fastest of them (after Chandra) to move through the chart.",
    knowledge: [
      "Classical sources call Budh kumara — youthful — and associate him with study, discourse and the settlement of accounts in the widest sense of the word. He is read whenever a chart shows a phase of language, negotiation or short-form learning.",
      "Budh's day is Wednesday, his direction north, and his presiding form is read through Vishnu in many traditions and separately as Budh himself in others. Where classical sources disagree, we say so.",
      "The Vedic scheme places Budh in a specific relationship to the Sun — never more than one rashi away, because in the astronomical sky Mercury is never more than about 28° from the Sun. The tradition and the astronomy match here, which is one of the reasons the Vedic scheme remains a working system.",
    ],
    why_it_is_worn:
      "Classical sources associate emerald with Budh. It is the stone traditionally worn at the start of a commercial undertaking and by those whose work is in language or short-form learning.",
  },
  {
    slug: "guru",
    graha: "Guru",
    planet: "Jupiter",
    deva: "गुरु",
    vahana: "A majestic elephant",
    presiding_form: "Brihaspati — the teacher of the devas",
    day: "Thursday",
    direction: "North-east",
    rashis: ["dhanu", "meena"],
    principal_stone: "yellow-sapphire",
    upratna: ["yellow-topaz", "citrine"],
    catalog_slug: "guru",
    stone_slug: "yellow-sapphire",
    stone_name: "Yellow Sapphire",
    stone_hindi: "Pukhraj",
    accent: "#a8821c",
    pitch:
      "Yellow sapphire is the stone the tradition gives under Guru — at a marriage, at the start of formal study. It is the most-asked-for stone in India, and the most mis-sold. Ours arrive with a lab report you can check on the laboratory's own site before paying.",
    rudraksha_mukhi: [5],
    mantra: "ॐ बृं बृहस्पतये नमः",
    mantra_roman: "Oṁ Bṛṁ Bṛhaspataye Namaḥ",
    lede:
      "Guru — Jupiter — is the graha of counsel, expansion and the slow acquisition of understanding. He is the teacher-graha, and in the classical scheme he is read whenever a chart requires long study or extended counsel.",
    knowledge: [
      "Classical sources call Guru the guru of the devas — the teacher of the gods themselves. His day is Thursday, his direction north-east, and his mount an elephant, because his approach is patient, deliberate and durable.",
      "Where Guru is well-placed in a chart the classical texts describe wisdom-acquired-slowly and counsel-that-holds. Where he is afflicted the same texts describe over-confidence and mis-teaching. Both are attributions, not outcome claims.",
      "The five-mukhi rudraksha and yellow sapphire are both read under Guru. If a consultation flags Jupiter, one of these two is where the classical scheme sends the wearer. If budget is a concern, the classical scheme sends them to the rudraksha first.",
    ],
    why_it_is_worn:
      "Classical sources associate yellow sapphire with Guru. It is the stone traditionally given at a marriage, at the start of formal study, and at the beginning of a long teaching career. Same graha for the five-mukhi rudraksha — the tradition offers both.",
  },
  {
    slug: "shukra",
    graha: "Shukra",
    planet: "Venus",
    deva: "शुक्र",
    vahana: "A white horse",
    presiding_form: "Lakshmi",
    day: "Friday",
    direction: "South-east",
    rashis: ["vrishabha", "tula"],
    principal_stone: "diamond",
    upratna: ["white-sapphire", "quartz"],
    catalog_slug: "shukra",
    stone_slug: "white-sapphire",
    stone_name: "White Sapphire",
    stone_hindi: "Safed Pukhraj",
    accent: "#7d8499",
    pitch:
      "The classical scheme accepts white sapphire under Shukra in place of diamond — same graha, a fraction of the price, and not spiritually inferior in any source we would quote. Every piece is certified, and we would rather sell you the substitute than a diamond you did not need.",
    rudraksha_mukhi: [6, 13],
    mantra: "ॐ शुं शुक्राय नमः",
    mantra_roman: "Oṁ Śuṁ Śukrāya Namaḥ",
    lede:
      "Shukra — Venus — is the graha of beauty, made things and the pleasure of good agreement. He is the teacher of the asuras in the classical scheme, mirroring Guru who teaches the devas.",
    knowledge: [
      "Classical sources place Shukra opposite Guru in the graha scheme — both are teachers, but of different sides. His presiding form is Lakshmi in most sources; some traditions read Shukra himself as the deity. Where sources disagree, we say so.",
      "Shukra is read whenever a chart shows a phase of partnership, of commissioned work, or of aesthetic undertakings. His day is Friday, his direction south-east.",
      "Because diamond is expensive, the classical scheme accepts white sapphire as a substitute under Shukra. We would rather sell you a certified white sapphire than a diamond you did not need — the tradition does not require a premium here, and neither do we.",
    ],
    why_it_is_worn:
      "Classical sources associate diamond, and its accepted substitute white sapphire, with Shukra. Either is read under this graha; the substitute is not spiritually inferior in the classical scheme.",
  },
  {
    slug: "shani",
    graha: "Shani",
    planet: "Saturn",
    deva: "शनि",
    vahana: "A large black vulture",
    presiding_form: "Kalabhairava",
    day: "Saturday",
    direction: "West",
    rashis: ["makara", "kumbha"],
    principal_stone: "blue-sapphire",
    upratna: ["blue-tanzanite", "amethyst"],
    catalog_slug: "shani",
    stone_slug: "blue-sapphire",
    stone_name: "Blue Sapphire",
    stone_hindi: "Neelam",
    accent: "#2e4a86",
    pitch:
      "Blue sapphire is the Shani stone — and the most cautioned-about stone in the tradition, which is exactly why every one of ours comes with the classical trial period honoured and a report that verifies on the laboratory's own website. No pressure, stated prices, one-of-one stones.",
    rudraksha_mukhi: [7, 14],
    mantra: "ॐ शं शनैश्चराय नमः",
    mantra_roman: "Oṁ Śaṁ Śanaiścarāya Namaḥ",
    lede:
      "Shani — Saturn — is the graha of discipline, delay and long labour rewarded slowly. He is the most cautioned-about graha in the classical texts, and the one most consulted about at the start of long undertakings.",
    knowledge: [
      "Classical sources place Shani at the boundary of the visible grahas — the slowest-moving, the furthest, and the one whose effects last the longest. His day is Saturday, his direction west, and his presiding form Kalabhairava, associated with time and the settling of a chart.",
      "The Vedic scheme reserves particular caution for Shani because he is read as the graha of consequence. Where he is well-placed, long labour is rewarded. Where he is afflicted, the same texts describe delay without conclusion. Both are what the sources say and both are attributions.",
      "Because blue sapphire is the stone most cautioned-about in the tradition — worn well by those Shani agrees with, badly by those he does not — the classical scheme recommends a trial period before permanent wearing. That is a real tradition, not a marketing device, and we do not price a trial separately.",
    ],
    why_it_is_worn:
      "Classical sources associate blue sapphire with Shani. It is traditionally given at the start of undertakings that will take years, and to those whose chart shows a strong Saturn placement.",
  },
  {
    slug: "rahu",
    graha: "Rahu",
    planet: "Rahu (north lunar node)",
    deva: "राहु",
    vahana: "A chariot drawn by black serpents",
    presiding_form: "Sarpa — the serpent form",
    day: "Saturday (with Shani)",
    direction: "South-west",
    rashis: ["kumbha"],
    principal_stone: "hessonite",
    upratna: ["orange-zircon"],
    catalog_slug: "rahu",
    stone_slug: "hessonite",
    stone_name: "Hessonite",
    stone_hindi: "Gomed",
    accent: "#a3611e",
    pitch:
      "Hessonite — the honey-orange gomed — is the classical Rahu stone. It is one of the most affordable of the nine principal stones, and each piece we hold is listed with origin, treatment and a report number you can check on the laboratory's own site.",
    rudraksha_mukhi: [8],
    mantra: "ॐ रां राहवे नमः",
    mantra_roman: "Oṁ Rāṁ Rāhave Namaḥ",
    lede:
      "Rahu is a shadow-graha — not a physical body but the north lunar node, where the Moon's orbit crosses the ecliptic. Classical sources treat him as fully real; he casts no light of his own, and is read as such.",
    knowledge: [
      "Rahu and Ketu are the chaya-grahas, the shadow-grahas. They are astronomically nodes, not bodies, but the Vedic scheme reads them as if they were bodies because their effect on eclipses (and therefore on the classical calendar) is decisive.",
      "Rahu is the north node — the ascending node — and is associated in the tradition with the direction the mind moves toward, whether the wearer likes it or not. His attributions are confusion, appetite and fixation.",
      "Hessonite — the honey-orange grossular garnet — is the classical stone for Rahu. It is not worn to gain anything but to settle what cannot be put down.",
    ],
    why_it_is_worn:
      "Classical sources associate hessonite with Rahu. It is traditionally worn during periods that require steadiness rather than acquisition. We do not say hessonite calms Rahu — the tradition places it under him.",
  },
  {
    slug: "ketu",
    graha: "Ketu",
    planet: "Ketu (south lunar node)",
    deva: "केतु",
    vahana: "A coiled black serpent",
    presiding_form: "Chintamani — a form associated with detachment",
    day: "Tuesday (with Mangal)",
    direction: "North-west",
    rashis: ["dhanu", "meena"],
    principal_stone: "cats-eye",
    upratna: ["chrysoberyl"],
    catalog_slug: "ketu",
    stone_slug: "cats-eye",
    stone_name: "Cat's Eye",
    stone_hindi: "Lehsunia",
    accent: "#877a38",
    pitch:
      "Cat's-eye chrysoberyl is the Ketu stone. The chatoyant band across the cabochon is what the laboratory grades and what you pay for — every piece we hold is certified, photographed honestly, and priced per carat in the open.",
    rudraksha_mukhi: [9],
    mantra: "ॐ कें केतवे नमः",
    mantra_roman: "Oṁ Keṁ Ketave Namaḥ",
    lede:
      "Ketu is the second shadow-graha — the south lunar node — and the classical counterpart to Rahu. Where Rahu is fixation, Ketu is release: the part of a life that is finished.",
    knowledge: [
      "Ketu is astronomically the south node, the descending node of the Moon's orbit. He and Rahu are always exactly opposite in the zodiac, moving retrograde together.",
      "Classical sources read Ketu as the graha of detachment. He is not read for gain but for closing — where a chapter is ending, Ketu is the graha under which the ending sits. His attribution is release, not loss; the tradition is careful with that word.",
      "Cat's-eye chrysoberyl is the classical stone for Ketu. Its chatoyant band is the diagnostic feature and the price driver: a sharp band across the cabochon is what the trade grades for.",
    ],
    why_it_is_worn:
      "Classical sources associate cat's-eye with Ketu. It is traditionally worn at the close of a chapter — not to hasten it, but to hold it steady.",
  },
];

export function grahaBySlug(slug) {
  return GRAHA_DETAILS.find((g) => g.slug === slug || g.catalog_slug === slug);
}
